export type FieldValueOnLeaveInput = {
  operator: string;
  inputType?: string;
  typedText: string;
  value: unknown;
  valueDisplay: string | string[] | null | undefined;
};

export type FieldValueOnLeaveResult = {
  nextFilterQuery: string;
  /** Display text to write. `null` leaves the stored query unchanged. `''` clears it. */
  commitTypedText: string | null;
  /** Search value written with `commitTypedText`. `''` keeps the row out of the search. */
  storedValue: string | null;
  showUnselectedHint: boolean;
};

const UNCHANGED: FieldValueOnLeaveResult = {
  nextFilterQuery: '',
  commitTypedText: null,
  storedValue: null,
  showUnselectedHint: false,
};

export function resolveFieldValueOnLeave(input: FieldValueOnLeaveInput): FieldValueOnLeaveResult {
  const typedText = input.typedText.trim();
  const inputType = input.inputType ?? 'input';

  if (isMultiValueOperator(input.operator)) {
    return {...UNCHANGED, nextFilterQuery: ''};
  }

  if (input.operator === 'in path') {
    return {...UNCHANGED, nextFilterQuery: selectedDisplay(input.valueDisplay)};
  }

  if (!isEqualityOperator(input.operator)) {
    return {...UNCHANGED, nextFilterQuery: typedText};
  }

  if (typedText === '') {
    if (!hasStoredValue(input.value) && selectedDisplay(input.valueDisplay) === '') {
      return UNCHANGED;
    }
    return {nextFilterQuery: '', commitTypedText: '', storedValue: '', showUnselectedHint: false};
  }

  const display = selectedDisplay(input.valueDisplay);
  if (typedText === display || typedText === visibleNameWithoutInvalidMarkers(display).trim()) {
    return {...UNCHANGED, nextFilterQuery: display};
  }

  if (isObjectPicker(inputType)) {
    return {
      nextFilterQuery: '',
      commitTypedText: '',
      storedValue: '',
      showUnselectedHint: false,
    };
  }

  return {
    nextFilterQuery: typedText,
    commitTypedText: typedText,
    storedValue: escapesLiteralText(inputType) ? escapeLiteralSearchValue(typedText) : typedText,
    showUnselectedHint: false,
  };
}

export function escapeLiteralSearchValue(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

/** Removes every invalid-record marker so the field and the leave check show the same name. */
export function visibleNameWithoutInvalidMarkers(name: string): string {
  return name.replace(/<\/?del>/g, '');
}

function isMultiValueOperator(operator: string): boolean {
  return operator === 'in' || operator === 'not in';
}

function isEqualityOperator(operator: string): boolean {
  return operator === '=' || operator === '<>';
}

function isObjectPicker(inputType: string): boolean {
  return inputType === 'map_id'
    || inputType === 'ref_array'
    || inputType === 'search_selector'
    || inputType === 'search_selector_multiple';
}

function escapesLiteralText(inputType: string): boolean {
  return inputType === 'input' || inputType === 'text_area';
}

function hasStoredValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return true;
}

function selectedDisplay(valueDisplay: string | string[] | null | undefined): string {
  if (Array.isArray(valueDisplay)) {
    return '';
  }
  return valueDisplay ?? '';
}
